import { Badge } from '@crowdfpl/ui-web/badge';
import { Card, CardContent } from '@crowdfpl/ui-web/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@crowdfpl/ui-web/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@crowdfpl/ui-web/tabs';
import { Bot, ChevronDown } from 'lucide-react';
import { useId, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UsernameWithXLink } from '@/components/shared/UsernameWithXLink';
import { cn } from '@/helpers/utils';
import { useDuelAiPredictionsOpenPreference } from '@/hooks/use-duel-ai-predictions-open-preference';
import { useIsMobile } from '@/hooks/utils/use-mobile';
import type { AiPrediction } from '@/types/duels';

function AiBotRow({ prediction }: { prediction: AiPrediction }) {
  return (
    <Link
      to={`/profile/${prediction.username}`}
      className="flex items-center gap-2 rounded-md border border-border/40 bg-background/60 px-2.5 py-2 transition-colors hover:bg-muted/50"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/50 bg-muted/80">
        <Bot className="h-4 w-4 text-muted-foreground" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">
          <UsernameWithXLink
            username={prediction.username}
            twitterUsername={null}
            showTwitterUsername={false}
            iconClassName="h-3 w-3"
            insideLink
            trackSource="duel_ai_predictions"
          />
        </div>
      </div>
      <Badge variant="outline" className="shrink-0 text-xs tabular-nums">
        {prediction.predictor_rating}
      </Badge>
    </Link>
  );
}

interface AiPredictionsSectionProps {
  aiPredictions: AiPrediction[];
  playerAId: string;
  playerBId: string;
  playerAName: string;
  playerBName: string;
}

export function AiPredictionsSection({
  aiPredictions,
  playerAId,
  playerBId,
  playerAName,
  playerBName,
}: AiPredictionsSectionProps) {
  const isMobile = useIsMobile();
  const panelId = useId();
  const [open, setOpen] = useDuelAiPredictionsOpenPreference();

  const { forA, forB, unmatchedCount } = useMemo(() => {
    const a: AiPrediction[] = [];
    const b: AiPrediction[] = [];
    for (const p of aiPredictions) {
      if (p.selected_player_id === playerAId) {
        a.push(p);
      } else if (p.selected_player_id === playerBId) {
        b.push(p);
      }
    }
    const unmatchedCount = Math.max(0, aiPredictions.length - a.length - b.length);
    return { forA: a, forB: b, unmatchedCount };
  }, [aiPredictions, playerAId, playerBId]);

  const column = (title: string, predictions: AiPrediction[]) => (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="space-y-2">
        {predictions.length === 0 ? (
          <p className="text-[11px] text-muted-foreground/80 leading-snug">No AI picks</p>
        ) : (
          predictions.map((p, i) => (
            <AiBotRow key={`${p.username}-${p.selected_player_id}-${i}`} prediction={p} />
          ))
        )}
      </div>
    </div>
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="border-muted bg-muted/30">
        <CardContent className="p-0">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors',
                'hover:bg-muted/40 cursor-pointer',
              )}
              aria-controls={panelId}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Bot className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="text-sm font-semibold text-foreground">AI Predictions</span>
                <span className="text-xs text-muted-foreground">({aiPredictions.length})</span>
              </div>
              <ChevronDown
                className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
                aria-hidden
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent id={panelId}>
            <div className="border-t border-border/40 px-4 pb-4 pt-2">
              {isMobile ? (
                <Tabs defaultValue="playerA" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-3 h-auto p-1">
                    <TabsTrigger value="playerA" className="text-xs w-full h-7 py-0">
                      {playerAName} ({forA.length})
                    </TabsTrigger>
                    <TabsTrigger value="playerB" className="text-xs w-full h-7 py-0">
                      {playerBName} ({forB.length})
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="playerA" className="mt-0">
                    {column(playerAName, forA)}
                  </TabsContent>
                  <TabsContent value="playerB" className="mt-0">
                    {column(playerBName, forB)}
                  </TabsContent>
                </Tabs>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
                  {column(playerAName, forA)}
                  {column(playerBName, forB)}
                </div>
              )}
              {unmatchedCount > 0 ? (
                <p className="mt-3 text-[10px] leading-snug text-muted-foreground/70">
                  {unmatchedCount} prediction{unmatchedCount === 1 ? '' : 's'} could not be matched to a player in this duel.
                </p>
              ) : null}
              <p className="mt-4 text-[10px] leading-snug text-muted-foreground/80">
                LLM votes are excluded from Crowd Power, manager ratings, and vote counts.
              </p>
            </div>
          </CollapsibleContent>
        </CardContent>
      </Card>
    </Collapsible>
  );
}
